import { Box, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import { DiscoverViewContext } from "contexts/DiscoverView";
import { OrganizationUserContext } from "contexts/Organization";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext } from "react";
import { UpgradeDialogSource } from "schemas/dashboard";
import { Plans } from "schemas/payments";

import MobilePlanCardAutopilot from "./Plan/MobilePlanCard/Autopilot";
import MobilePlanCardPro from "./Plan/MobilePlanCard/Pro";
import MobilePlanCardVirtualAssistant from "./Plan/MobilePlanCard/VirtualAssistant";
import AutopilotPlanCard from "./Plan/PlanCard/Autopilot";
import FreePlanCard from "./Plan/PlanCard/Free";
import ProPlanCard from "./Plan/PlanCard/Pro";
import VirtualAssistantPlanCard from "./Plan/PlanCard/VirtualAssistant";
import { getAllPlans } from "./Plan/helpers";

export default function Subscription({
  upgradeDialogSource,
}: {
  upgradeDialogSource: UpgradeDialogSource | null;
}) {
  const theme = useTheme();
  const { discoverTab } = useContext(DiscoverViewContext);
  const isMediumScreen = useMediaQuery("(max-width:1300px)");
  const isLargeScreen = useMediaQuery(theme.breakpoints.down("lg"));
  const isMobileScreen =
    useMediaQuery(theme.breakpoints.down("md")) ||
    (isLargeScreen && discoverTab !== null);
  const { plans, tab } = useContext(SubscriptionContext);
  const { currentOrg } = useContext(OrganizationUserContext);

  const plansToDisplay = getAllPlans(
    tab,
    upgradeDialogSource,
    currentOrg?.showAutopilotPlan,
  );

  const getGrid = () => {
    const totalPlans = plansToDisplay?.length;
    const columnByPlan = 12 / (totalPlans || 1);
    if (isMobileScreen) return 12;
    if (discoverTab !== null || isMediumScreen) {
      return Math.max(6, columnByPlan);
    }
    if (isLargeScreen) return Math.max(4, columnByPlan);
    return columnByPlan;
  };

  const renderPlan = (plan: Plans) => {
    if (plan === Plans.FREE) {
      return (
        <Grid key={plan} item xs={12} md={getGrid()}>
          <FreePlanCard plan={plan} />
        </Grid>
      );
    } else if (plan === Plans.PRO || plan === Plans.PRO_YEARLY) {
      if (isMobileScreen && upgradeDialogSource) {
        return (
          <Grid item xs={12}>
            <MobilePlanCardPro />
          </Grid>
        );
      }
      return (
        <Grid key={plan} item xs={12} md={getGrid()}>
          <ProPlanCard plan={plan} />
        </Grid>
      );
    } else if (plan === Plans.VA || plan === Plans.VA_YEARLY) {
      if (isMobileScreen && upgradeDialogSource) {
        return (
          <Grid item xs={12}>
            <MobilePlanCardVirtualAssistant />
          </Grid>
        );
      }
      return (
        <Grid key={plan} item xs={12} md={getGrid()}>
          <VirtualAssistantPlanCard plan={plan} />
        </Grid>
      );
    } else if (plan === Plans.AUTOPILOT || plan === Plans.AUTOPILOT_YEARLY) {
      if (isMobileScreen && upgradeDialogSource) {
        return (
          <Grid item xs={12}>
            <MobilePlanCardAutopilot />
          </Grid>
        );
      }
      return (
        <Grid key={plan} item xs={12} md={getGrid()}>
          <AutopilotPlanCard plan={plan} />
        </Grid>
      );
    } else {
      return <></>;
    }
  };

  return plans ? (
    <>
      <Grid container spacing={1}>
        {plansToDisplay?.map((plan) => renderPlan(plan as Plans))}
      </Grid>
      {upgradeDialogSource && (
        <Box sx={{ mt: 4, px: 1 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, mb: 2, textAlign: "center" }}
          >
            How Credit Rollover Works
          </Typography>
          <Grid container spacing={2}>
            {[
              {
                n: "1",
                title: "Send emails each month",
                desc: "Use your monthly email allotment to pitch brands as usual.",
              },
              {
                n: "2",
                title: "Unused credits carry forward",
                desc: "At the end of your billing cycle, any unused emails automatically roll over to next month.",
              },
              {
                n: "3",
                title: "Build up your sending power",
                desc: "Accumulate up to 1,000 rolled-over credits on top of your monthly allotment.",
              },
            ].map((step) => (
              <Grid item xs={12} md={4} key={step.n}>
                <Box
                  sx={{
                    backgroundColor: "#f0fdf4",
                    borderRadius: 3,
                    p: 2,
                    height: "100%",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: 22,
                      color: "#16a34a",
                      mb: 0.5,
                    }}
                  >
                    {step.n}
                  </Typography>
                  <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 0.5 }}>
                    {step.title}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                    {step.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </>
  ) : (
    <></>
  );
}
